import React from 'react'

/**
 * Columnas compartidas por TODAS las tablas del centralizador.
 *
 * Antes cada tabla ponía lo mismo en distinto orden (una arrancaba por material, otra por marca, otra
 * no mostraba el material) y costaba leerlas juntas. Acá vive el orden único y el formato único:
 *
 *      Material · Marca · Modelo · Año · Color · Zona · PVP · IVA · (lo propio de cada tabla) · Acciones
 *
 * Cada tabla arma sus columnas con `columnasBase([...])` y después agrega las suyas.
 */

export const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`

export const pct = (v) =>
  v === null || v === undefined || v === '' ? '—' : `${(Number(v) * 100).toFixed(1).replace('.0', '')}%`

const vacio = <span style={{ color: '#9aa5b1' }}>—</span>

/** Primer valor no vacío entre varios nombres de campo (los SPs no siempre usan el mismo). */
const pick = (row, claves) => {
  for (let i = 0; i < claves.length; i += 1) {
    const v = row[claves[i]]
    if (v !== null && v !== undefined && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

// ----------------------------------------------------------------------------------------------------
// Colores: el nombre que manda SAP, pero además el color de verdad. Un punto de color se distingue de
// un vistazo; "GRIS PLATA MATE" vs "GRIS NARDO NEGRO MATE" en texto plano no.
// ----------------------------------------------------------------------------------------------------
const PALETA = [
  ['NEGR', '#1c1c1e'], ['BLANC', '#f2f3f5'], ['PLATA', '#c3c8cd'], ['GRIS', '#8d949c'],
  ['ROJ', '#d32f2f'], ['ESCARLATA', '#c62828'], ['VINOTINTO', '#7b1e28'],
  ['AZUL', '#1565c0'], ['CELESTIAL', '#7bb0e8'], ['CELESTE', '#7bb0e8'], ['TURQUESA', '#00897b'],
  ['VERDE', '#2e7d32'], ['ARMY', '#4b5320'], ['CAMO', '#5a6b3b'], ['LIMA', '#9ccc65'],
  ['AMARILL', '#f9a825'], ['NARANJA', '#ef6c00'], ['DORAD', '#c9a227'], ['BRONCE', '#a1683a'],
  ['CAFE', '#6d4c41'], ['MARRON', '#6d4c41'], ['BEIGE', '#d7ccc8'], ['ARENA', '#d9c7a3'],
  ['MORAD', '#6a1b9a'], ['VIOLET', '#6a1b9a'], ['ROSA', '#d81b60'], ['FUCSIA', '#c2185b'],
  ['TITANIO', '#7d8791'], ['NEBULOS', '#4a4f55'], ['NARDO', '#b9bcc0'], ['ACERO', '#6b7684'],
]

/** Devuelve hasta 2 colores del nombre ("GRIS NARDO NEGRO MATE" -> gris + negro). */
export const coloresDe = (nombre) => {
  const txt = String(nombre || '').toUpperCase()
  const hits = []
  PALETA.forEach(([clave, hex]) => {
    const i = txt.indexOf(clave)
    if (i >= 0 && !hits.some((h) => h.hex === hex)) hits.push({ i, hex })
  })
  return hits.sort((a, b) => a.i - b.i).slice(0, 2).map((h) => h.hex)
}

/** Punto de color. Con dos colores lo parte por la mitad, como la moto real. */
export const PuntoColor = ({ nombre, size = 12 }) => {
  const cs = coloresDe(nombre)
  if (!cs.length) return null
  const background = cs.length > 1 ? `linear-gradient(135deg, ${cs[0]} 50%, ${cs[1]} 50%)` : cs[0]
  return (
    <span
      title={nombre}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background,
        border: '1px solid rgba(15,36,57,.28)',
        display: 'inline-block',
        flex: '0 0 auto',
      }}
    />
  )
}

/** Chip genérico, mismo look en todas las tablas. */
export const chip = (txt, bg, color) => (
  <span style={{ background: bg, color, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
    {txt}
  </span>
)

// ----------------------------------------------------------------------------------------------------
// Las columnas base, en el orden único
// ----------------------------------------------------------------------------------------------------
const BASE = {
  /** Material: obligatorio en todas las tablas. En las que van por modelo, la lista de códigos. */
  material: {
    id: 'material',
    header: 'Material',
    size: 150,
    accessorFn: (r) => pick(r, ['material', 'materiales_texto', 'codigo_sap']),
    Cell: ({ row }) => {
      const txt = pick(row.original, ['material', 'materiales_texto', 'codigo_sap'])
      if (!txt) return vacio
      const codigos = txt.split(', ')
      if (codigos.length === 1) return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{txt}</span>
      // varios colores = varios códigos: se muestra el primero y el resto en el tooltip
      return (
        <span title={txt} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {codigos[0]}
          <span style={{ color: '#6b7684' }}> +{codigos.length - 1}</span>
        </span>
      )
    },
  },
  marca: {
    id: 'marca',
    header: 'Marca',
    size: 105,
    accessorFn: (r) => pick(r, ['marca']),
    Cell: ({ row }) => pick(row.original, ['marca']) || vacio,
  },
  modelo: {
    id: 'modelo',
    header: 'Modelo',
    size: 230,
    accessorFn: (r) => pick(r, ['modelo', 'modelo_sap', 'modelo_corto']),
    Cell: ({ row }) => pick(row.original, ['modelo', 'modelo_sap', 'modelo_corto']) || vacio,
  },
  anio: {
    id: 'anio',
    header: 'Año',
    size: 70,
    accessorFn: (r) => Number(pick(r, ['anio_modelo', 'anio']) || 0) || '',
    Cell: ({ row }) => Number(row.original.anio_modelo) || vacio,
  },
  color: {
    id: 'color',
    header: 'Color',
    size: 175,
    accessorFn: (r) => pick(r, ['color', 'color_sap', 'colores_texto']),
    Cell: ({ row }) => {
      const txt = pick(row.original, ['color', 'color_sap', 'colores_texto'])
      if (!txt) return <span style={{ color: '#9aa5b1' }}>sin color</span>
      const partes = txt.split(', ')
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }} title={txt}>
          {partes.slice(0, 3).map((p) => <PuntoColor key={p} nombre={p} />)}
          <span>{partes.length > 1 ? `${partes.length} colores` : txt}</span>
        </span>
      )
    },
  },
  zona: {
    id: 'zona',
    header: 'Zona',
    size: 100,
    accessorFn: (r) => pick(r, ['zona']),
    Cell: ({ row }) => pick(row.original, ['zona']) || vacio,
  },
  /** PVP vigente. Si hay una propuesta de Trade sin publicar, se ve la diferencia acá mismo. */
  pvp: {
    id: 'pvp',
    header: 'PVP',
    size: 155,
    accessorFn: (r) => Number(pick(r, ['pvp', 'pvp_actual', 'pvp_vigente']) || 0),
    Cell: ({ row }) => {
      const r = row.original
      const vigente = Number(r.pvp_vigente !== undefined ? r.pvp_vigente : (r.pvp || r.pvp_actual || 0))
      const propuesto = Number(r.pvp_propuesto || 0)
      const hayPropuesta = propuesto > 0 && propuesto !== vigente
      if (!vigente && !propuesto) return <span style={{ color: '#d64545', fontWeight: 600 }}>sin precio</span>
      if (!hayPropuesta) return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(vigente)}</span>
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span style={{ color: '#9aa5b1', textDecoration: 'line-through', fontSize: 11 }}>{fmt(vigente)}</span>
          <span style={{ color: '#b26a00', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {fmt(propuesto)}
          </span>
        </span>
      )
    },
  },
  /** Lo que SAP tiene HOY (feed diario ZRSD009). Es el baseline contra el que Trade actualiza. */
  sap: {
    id: 'pvp_sap',
    header: 'Precio en SAP',
    size: 140,
    accessorFn: (r) => Number(r.pvp_sap || 0),
    Cell: ({ row }) => {
      const r = row.original
      const sap = Number(r.pvp_sap || 0)
      if (!sap) return <span style={{ color: '#9aa5b1' }}>sin precio en SAP</span>
      const vigente = Number(r.pvp_vigente !== undefined ? r.pvp_vigente : (r.pvp || r.pvp_actual || 0))
      const dispar = Number(r.pvp_sap_dispar) === 1
      const difiere = vigente > 0 && vigente !== sap
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: difiere ? '#b26a00' : '#2b3440' }}>
            {fmt(sap)}
          </span>
          {dispar && (
            <span style={{ fontSize: 10.5, color: '#d64545' }}>
              los colores traen precios distintos
            </span>
          )}
        </span>
      )
    },
  },
  iva: {
    id: 'iva',
    header: 'IVA',
    size: 70,
    accessorFn: (r) => pct(r.iva_pct),
  },
}

/**
 * Arma las columnas base en el orden único.
 *   columnasBase(['material', 'marca', 'modelo', 'anio', 'color', 'pvp'])
 * `over` permite pisar algo puntual sin romper el orden: columnasBase([...], { modelo: { size: 300 } })
 */
export const columnasBase = (claves, over = {}) =>
  Object.keys(BASE)
    .filter((k) => claves.includes(k))
    .map((k) => ({ ...BASE[k], ...(over[k] || {}) }))

/** Columna de acciones: siempre última y nunca filtrable/ordenable. */
export const colAcciones = (render, size = 200) => ({
  id: 'acciones',
  header: 'Acciones',
  size,
  enableColumnFilter: false,
  enableGlobalFilter: false,
  enableSorting: false,
  Cell: ({ row }) => render(row.original),
})
